
import { LeanToSettingConfig } from "../../../config/LeanToSettingConfig";
import { Building, WallSide } from "../../../building/Building";
import { RoofSideTrim } from "../../../building/trim/RoofSideTrim";
import { IRoofSideTrimMetrics } from "../../../building/trim/IRoofSideTrimMetrics";
import { Main } from "../../../Main";
import { BuildingGeometry } from "../../../building/BuildingGeometry";
import { MaterialManager } from "../../../material/MaterialManager";
import { Mesh, Vector2, Vector3, Object3D } from "three";
import { ISceneAddon } from "../../addon/ISceneAddon";
import { THREEUtils } from "../../../utils/THREEUtils";
import { ObjectUtils } from "../../../utils/data/ObjectUtils";
import { PostManager } from "../../../building/post/PostManager";
import { LeanToPost } from "./LeanToPost";
import { LeanToWraparound } from "./LeanToWraparound";
import { WarningWindow } from "../../../ui/WarningWindow";
import {InsetBaySetting} from "config/InsetBaySetting";
interface LeanToMetrics
{
	length               : number;
	drop                 : number;

	cut_front            : number;
	cut_back             : number;

	pitch                : number;
	tan                  : number;
	alpha                : number;

	top                  : number;
	roof_height          : number;
	wall_top             : number;

	building_length      : number;
	building_length_half : number;
	WALL_HEIGHT          : number;
}

/**
 * This is one instance of a lean to, that connects to one side of the house.
 * The positions are modeled for the "left" side in mind, the other sides
 * are created by rotating this side.
 */
export class LeanTo
{

	public readonly id: WallSide;
	private _isSide : boolean;

	private _config : LeanToSettingConfig;

	private _building : Building;

	private _metrics:LeanToMetrics = {
		length               : 0,
		drop                 : 0,

		cut_front            : 0,
		cut_back             : 0,

		pitch                : 0,
		tan                  : 0,
		alpha                : 0,

		top                  : 0,
		roof_height          : 0,
		wall_top             : 0,

		building_length      : 0,
		building_length_half : 0,
		WALL_HEIGHT          : 1
	};

	private _container : Object3D;
	private _roof : Mesh; // the top roof
	private _bottomRoof : Mesh; // the bottom part of the roof -> it's always white

	private _walls : {
		left   : Mesh;
		front  : Mesh;
		back   : Mesh;
		//bottom : Mesh;
	};
	private _endRoof : {
		front : Mesh;
		back  : Mesh;
	};

	private _postManager: PostManager;

	private _wraparoundPost : LeanToPost;

	private _wraparound_back : LeanToWraparound;
	private _wraparound_front : LeanToWraparound;

	private _trim : RoofSideTrim = null;
	private _trimMetrics : IRoofSideTrimMetrics;

	constructor(id: WallSide, config:LeanToSettingConfig, building:Building)
	{
		this.id = id;
		this._isSide = this.id === Building.WALLS.LEFT || this.id === Building.WALLS.RIGHT;

		this._config = config;
		this._building = building;

		this.createMeshes();

		this.addListeners();

		this.createTrim();
		this.update();

		Main.getInstance().render = true;
	}

	public addListeners()
	{
		this._config.enabled.signal.add(this.onEnabledChange);
		this._config.drop.signal.add(this.updateDrop);
		this._config.length.signal.add(this.update);
		this._config.pitch.signal.add(this.update);

		this._config.cut_front.signal.add(this.updateCut1);
		this._config.cut_back.signal.add(this.updateCut2);

		this._config.wraparound_back.enabled.signal.add(this.update);
		this._config.wraparound_front.enabled.signal.add(this.update);

		Main.getInstance().config.building.single_slope.signal.add(this.update);

		const insetBayConfig = Main.getInstance().config.building.insetBay;
		insetBayConfig.front.count.signal.add(this.update);
		insetBayConfig.front.length_1.signal.add(this.update);
		insetBayConfig.front.length_2.signal.add(this.update);
		insetBayConfig.back.count.signal.add(this.update);
		insetBayConfig.back.length_1.signal.add(this.update);
		insetBayConfig.back.length_2.signal.add(this.update);

		const buildingConfig = Main.getInstance().config.building;
		buildingConfig.leanto.wall.signal.add(this.update);
		buildingConfig.sizeChange.add(this.update);
	}

	public removeListeners()
	{
		this._config.enabled.signal.remove(this.onEnabledChange);
		this._config.drop.signal.remove(this.updateDrop);
		this._config.length.signal.remove(this.update);
		this._config.pitch.signal.remove(this.update);

		this._config.cut_front.signal.remove(this.updateCut1);
		this._config.cut_back.signal.remove(this.updateCut2);

		this._config.wraparound_back.enabled.signal.remove(this.update);
		this._config.wraparound_front.enabled.signal.remove(this.update);

		Main.getInstance().config.building.single_slope.signal.remove(this.update);

		const insetBayConfig = Main.getInstance().config.building.insetBay;
		insetBayConfig.front.count.signal.remove(this.update);
		insetBayConfig.front.length_1.signal.remove(this.update);
		insetBayConfig.front.length_2.signal.remove(this.update);
		insetBayConfig.back.count.signal.remove(this.update);
		insetBayConfig.back.length_1.signal.remove(this.update);
		insetBayConfig.back.length_2.signal.remove(this.update);

		const buildingConfig = Main.getInstance().config.building;
		buildingConfig.leanto.wall.signal.remove(this.update);
		buildingConfig.sizeChange.remove(this.update);
	}

	private updateDrop = () =>
	{
		this.update("drop");
	};

	private updateCut1 = () =>
	{
		this.update("cut 1");
	};

	private updateCut2 = () =>
	{
		this.update("cut 2");
	};

	private createMeshes()
	{
		this.createContainer();
		this.createWalls();
		this.createRoof();
		this.createPosts();
		this.createWraparounds();
	}

	private createContainer()
	{
		// container
		this._container = new Object3D();

		var containerRotationY = 0; // left

		// #1 cases
		switch (this.id)
		{
			case Building.WALLS.RIGHT:
				containerRotationY = Math.PI;
				break;
			case Building.WALLS.FRONT:
				containerRotationY = Math.PI/2;
				break;
			case Building.WALLS.BACK:
				containerRotationY = -Math.PI/2;
				break;
		}

		// #2 calculation: should be adjusted to work for "back"
		/*
		 var normal:Vector3 = Building.NORMALS[this.id];
		 var left_axis = new Vector3(-1, 0, 0);
		 var containerRotationY = Math.acos(normal.dot(left_axis)); // the default orientation is left
		 var y = new Vector3(0, 1, 0);
		 var c = new Vector3();
		 c.crossVectors(normal, left_axis);

		 if (c.dot(y) > 0)
		 {
		 containerRotationY *= -1;
		 }
		 */

		this._container.rotation.y = containerRotationY;
	}

	private createWalls()
	{
		var sideGeometry = BuildingGeometry.createWallGeometry();
		var endGeometry  = BuildingGeometry.createWallGeometry();

		var material = MaterialManager.getInstance().wall_doubleSided;

		var left   = new Mesh(sideGeometry, material);
		var front  = new Mesh(endGeometry,  material);
		var back   = new Mesh(endGeometry,  material);

		left.castShadow = true;
		front.castShadow = true;
		back.castShadow = true;

		left.rotation.y = Math.PI / 2;
		back.rotation.y = Math.PI;

		this._container.add(left);
		this._container.add(front);
		this._container.add(back);

		this._walls = {
			left  : left,
			front : front,
			back  : back
		};
	}

	private createRoof()
	{
		this.createTopRoof();
		this.createEndRoof();
	}

	private createTopRoof()
	{
		var materials = this._building.materials;
		var roofGeometry = BuildingGeometry.createRoofGeometry();

		this._roof = new Mesh(roofGeometry, materials.roof_singleSided);
		this._roof.castShadow = true;
		this._container.add(this._roof);

		this._bottomRoof = new Mesh(roofGeometry, materials.interior);
		this._container.add(this._bottomRoof);
	}

	private createEndRoof()
	{
		var endRoofGeometry = BuildingGeometry.createEndRoofGeometry(false);
		var material = MaterialManager.getInstance().wall_doubleSided;

		var frontRoof = new Mesh(endRoofGeometry, material);
		var backRoof  = new Mesh(endRoofGeometry, material);

		this._container.add(frontRoof);
		this._container.add(backRoof);

		this._endRoof = {
			front : frontRoof,
			back  : backRoof
		};
	}

	private createPosts()
	{
		this._postManager = new PostManager(this._isSide, {}, this);
		this._container.add(this._postManager.container);
	}

	private createTrim()
	{
		this._trim = new RoofSideTrim({
			parent: this._container,
			pitch: this._config.pitch,
			downSpoutPadding: 0.5,
			isLeanto: true
		});
	}

	private createWraparounds()
	{
		this._wraparound_back = new LeanToWraparound({
			setting: this._config.wraparound_back,
			building: this._building,
			leanTo: this,
			bottom: false,
			container: this._container
		});
		this._wraparound_front = new LeanToWraparound({
			setting: this._config.wraparound_front,
			building: this._building,
			leanTo: this,
			bottom: true,
			container: this._container
		});

		this._container.add(this._wraparound_back.container);
		this._container.add(this._wraparound_front.container);
	}

	private onEnabledChange = ():void =>
    {
        if (this._config.enabled.value)
        {
			this._building.scene3D.add(this._container);
			this.update();
		}
		else
		{
			this.update();
            this._building.scene3D.remove(this._container);
		}

		Main.getInstance().render = true;
	};

	public isThereAConflictWithAddons = (): boolean =>
	{
		const addons = Main.getInstance().scene.building.addons[this.id];

		for (const addon of addons)
		{
			if (this.isThereAConflictWithAddon(addon))
			{
				return true;
			}
		}

		return false;
	};

	private isThereAConflictWithAddon = (addon: ISceneAddon): boolean =>
	{
		if (this._config.enabled.value)
		{
			const buildingHeight = Main.getInstance().config.building.height.value;

			if (addon.itemId.indexOf('window') === -1)
			{
				if (this.isAddonBetweenEdgesOfLeanto(addon))
				{
					if ((addon.getPosition().vertical + addon.height / 2) > (buildingHeight - this._config.drop.value))
					{
						return true;
					}
				}
			}
		}

		return false;
	};

	private isAddonBetweenEdgesOfLeanto = (addon: ISceneAddon): boolean =>
	{
		const buildingSize = this._isSide ? Main.getInstance().config.building.length.value : Main.getInstance().config.building.width.value;
		const cut_1 = this._config.cut_front.value;
		const cut_2 = this._config.cut_back.value;

		/** The namings have been set with the left wall in mind */

		const edge_right_of_addon = addon.getPosition().horizontal + addon.width / 2;
		const edge_left_of_addon = addon.getPosition().horizontal - addon.width / 2;

		const edge_right_of_leanto =  buildingSize / 2 - cut_1;
		const edge_left_of_leanto = -buildingSize / 2 + cut_2;

		return edge_left_of_addon <= edge_right_of_leanto && edge_right_of_addon >= edge_left_of_leanto;
	};

	private updateMetrics()
	{
		var config           = this._config;
		var metrics          = this._metrics;
		var building_metrics = this._building.config.metrics;

		metrics.length       = config.length.value;
		metrics.drop         = config.drop.value;

		metrics.cut_front    = config.cut_front.value;
		metrics.cut_back     = config.cut_back.value;

		metrics.pitch        = config.pitch.value;
		metrics.tan          = metrics.pitch/12;
		metrics.alpha        = Math.atan(metrics.tan);

		metrics.top          = building_metrics.height - metrics.drop;
		metrics.roof_height  = config.length.value * metrics.tan;
		metrics.wall_top     = metrics.top - metrics.roof_height;

		metrics.building_length      = this.getBuildingLength() - metrics.cut_back - metrics.cut_front;
		metrics.building_length_half = metrics.building_length/2;
	}

	/**
	 * Call if any of these change:
	 * - building size (width, length, height)
	 * - config values (length, drop, pitch)
	 * - enabled / disabled state
	 * - wall enabled / disabled state
	 */
	private update = async (type?: string) =>
	{
        if (!this._container.parent || !this._trim)
		{
			// not enabled yet or addons not loaded yet
			return;
		}

		if (this.isThereAConflictWithAddons())
		{
			if (type)
			{
				const maxmin = type === 'drop' ? 'maximum' : 'minimum';
				await WarningWindow.open(`You have met the ${maxmin} ${type} due to interference with another component.`);
				this._config[type].setValue(this._config[type].value - 1);
			}
		}

		/*
		 check if the lean-to is below the earth
		 building height and drop values can move the lean-to below the earth
		 */
		const buildingHeight = Main.getInstance().config.building.height.value;

		if (Math.abs(buildingHeight - this._config.drop.value) < 3) {
			// check the drop value
			if (type) {
				const maxmin = type === 'drop' ? 'maximum' : 'minimum';
				await WarningWindow.open(`You have met the ${maxmin} ${type}.`);
				this._config[type].setValue(this._config[type].value - 1);
			}
			// check the building height value
			else{
				await WarningWindow.open(`You have met the minimum building height for the selected lean-to(s).`);
				Main.getInstance().config.building.height.setValue(buildingHeight+1)
				Main.getInstance().toolsView.undoBuildingHeightChange(buildingHeight+1);
			}
		}

		this.updateMetrics();
		if (this.id === "left" || this.id && !Main.getInstance().config.building.single_slope.value)
		{
			const overhang_sides = <HTMLInputElement>document.getElementById("overhang_sides");
			if (this._metrics.drop === 0 && Main.getInstance().config.building.leanto[this.id].enabled.value === true)
			{
				Main.getInstance().config.building.overhang_sides.setValue(0);
				(<HTMLInputElement>overhang_sides.children[0]).value = "0";
				(<HTMLInputElement>(<HTMLDivElement>overhang_sides.children[1]).children[0]).value = "0";
				overhang_sides.classList.add("disabled");
			}
			else
			{
				overhang_sides.classList.remove("disabled");
			}
		}

		if ((this.id === "left" && Main.getInstance().config.building.leanto.left.enabled.value === false) || (this.id && (Main.getInstance().config.building.leanto.right.enabled.value === false || Main.getInstance().config.building.single_slope.value)))
		{
			document.getElementById("overhang_sides").classList.remove("disabled");
		}


        var originalBuildingLength = this._metrics.building_length;

		// update container position
		var building_width_half = this.getBuildingWidth() / 2;
		var normal:Vector3 = Building.NORMALS[this.id];
		const v = normal.clone().multiplyScalar(building_width_half);
		this._container.position.set(v.x, v.y, v.z);

		var front_edge = building_width_half - this._metrics.cut_front;
		var back_edge = -building_width_half + this._metrics.cut_back;
		var pos = (front_edge + back_edge) / 2;

		var insetBays = Main.getInstance().config.building.insetBay;
		var frontBays = insetBays.front;
		var backBays  = insetBays.back;

		if (this._isSide)
		{
			var zPosOffset = 0;
			var zScaleOffset = 0;


			if (frontBays.count.value >= 1)
			{
				zPosOffset += frontBays.length_1.value / 2;
				zScaleOffset += frontBays.length_1.value;
			}
			if (frontBays.count.value >= 2)
			{
				zPosOffset += frontBays.length_2.value / 2;
				zScaleOffset += frontBays.length_2.value;
			}
			if (backBays.count.value >= 1)
			{
				zPosOffset -= backBays.length_1.value / 2;
				zScaleOffset += backBays.length_1.value;
			}
			if (backBays.count.value >= 2)
			{
				zPosOffset -= backBays.length_2.value / 2;
				zScaleOffset += backBays.length_2.value;
			}

			this._metrics.building_length += zScaleOffset;
			this._metrics.building_length_half = this._metrics.building_length / 2;
			this._container.position.z = pos + zPosOffset;
		}
		else
		{
			this._container.position.x = pos;

			var zOffset = 0;

			let bays: InsetBaySetting;
			var negative = false;
			if (this.id === Building.WALLS.FRONT)
			{
				bays = insetBays.front;
			}
			else if (this.id === Building.WALLS.BACK)
			{
				bays = insetBays.back;
				negative = true;
			}

			if (bays)
			{
				if (bays.count.value >= 1)
				{
					zOffset += bays.length_1.value;
				}
				if (bays.count.value >= 2)
				{
					zOffset += bays.length_2.value;
				}

				if (negative)
				{
					zOffset = -zOffset;
				}
			}
			this._container.position.z += zOffset;

		}

		this.updatePosts(originalBuildingLength, this._container.position.z);

		// needs to be called after updatePosts
		this.updateTrim();

		// needs to be called before updateRoof and updateWall
		this.updateWallRoofVisibility();

		this.updateRoof();

		// needs to be called after updateTrim
		this.updateWalls();

		this.updateWraparounds();

		Main.getInstance().render = true;
	};

	private updateWallRoofVisibility()
	{
		// updateRoof and updateWalls use the visible values set here
		var wallsAreVisible = Main.getInstance().config.building.leanto.wall.value;

		this._walls.back.visible    = wallsAreVisible;
		this._walls.front.visible   = wallsAreVisible;
		this._walls.left.visible    = wallsAreVisible;

		this._endRoof.front.visible = wallsAreVisible && !this._config.wraparound_front.enabled.value;
		this._endRoof.back.visible  = wallsAreVisible && !this._config.wraparound_back.enabled.value;
	}

	private updateRoof()
	{
		var metrics           = this._metrics;
		var length            = metrics.length;
		var roof              = this._roof;
		var roof_side_scale_y = length / Math.cos(metrics.alpha);

		// top roof

		roof.scale.z    = metrics.building_length;
		roof.scale.y    = roof_side_scale_y;
		roof.position.y = metrics.top;
		roof.rotation.z = Math.PI / 2 + metrics.alpha;

		THREEUtils.scaleUVs(roof.geometry, new Vector2(metrics.building_length, roof_side_scale_y));

		// bottom roof

		var bottomRoof        = this._bottomRoof;
		bottomRoof.scale.z    = metrics.building_length;
		bottomRoof.scale.y    = roof_side_scale_y;
		bottomRoof.position.y = metrics.top;
		bottomRoof.rotation.z = Math.PI / 2 + metrics.alpha;

		//THREEUtils.scaleUVs(roof.geometry, new Vector2(metrics.building_length, roof_side_scale_y));

		// front roof

		var frontRoof = this._endRoof.front;
		if (frontRoof.visible)
		{
			frontRoof.scale.x    = length * 2;
			frontRoof.scale.y    = metrics.roof_height;
			//frontRoof.position.x = - 10;
			frontRoof.position.z = metrics.building_length_half;
			frontRoof.position.y = metrics.wall_top;
		}

		// back roof

		var backRoof = this._endRoof.back;
		if (backRoof.visible)
		{
			backRoof.scale.x    = length * 2;
			//backRoof.rotation.y = Math.PI;

			backRoof.scale.y    = metrics.roof_height;
			backRoof.position.z = -metrics.building_length_half;
			backRoof.position.y = metrics.wall_top;
		}

		THREEUtils.scaleUVs(frontRoof.geometry, new Vector2(frontRoof.scale.x, frontRoof.scale.y));
	}

	// needs to be called after updateTrim (uses trimMetrics)
	private updateWalls()
	{
		// we could check any of back, front, left
		if (!this._walls.back.visible)
		{
			return;
		}

		var metrics = this._metrics;
		var trimMetrics = this._trimMetrics;

		var width  = metrics.length;
		var length = metrics.building_length; //trimMetrics.z_front - trimMetrics.z_back;

		var left  = this._walls.left;
		var front = this._walls.front;
		var back  = this._walls.back;

		const correction = 0.1;

		this.updateWall(left);
		left.scale.x = length;
		left.position.x = -width;
		//left.position.z = length/2;

		this.updateWall(front);
		front.scale.x = width;
		front.position.x = -width/2;
		front.position.z = trimMetrics.z_front + correction;

		this.updateWall(back);
		back.scale.x = width;
		back.position.x = -width/2;
		back.position.z = trimMetrics.z_back - correction;

		THREEUtils.scaleUVs(left.geometry,   new Vector2(left.scale.x,   left.scale.y));
		THREEUtils.scaleUVs(front.geometry,  new Vector2(front.scale.x,  front.scale.y));
	}

	private updateWall(wall:Mesh)
	{
		var metrics = this._metrics;

		wall.scale.y    = metrics.WALL_HEIGHT;
		wall.position.y = metrics.wall_top - metrics.WALL_HEIGHT / 2;
	}

	private updatePosts(length, zOffset)
	{
		var metrics = this._metrics;
		var height = metrics.wall_top - this._metrics.WALL_HEIGHT + 1;

		this._postManager.update(length, height, this.id, zOffset);

		this._postManager.container.position.x = -metrics.length + PostManager.post_width;

		// wraparound post if needed

		if (this._config.wraparound_front.enabled.value)
		{
			this._wraparoundPost = this._wraparoundPost || new LeanToPost();

			var post_container = this._wraparoundPost.container;
			post_container.rotation.x = -Math.PI/2;
			post_container.position.z = metrics.building_length / 2 +
			                            this._config.wraparound_front.getOtherLeanTo(this._config).length.value
			                            - 0.2;

			post_container.position.x = -metrics.length + PostManager.post_width;
			post_container.position.y = height/2;
			post_container.scale.z = height;
			post_container.scale.x = post_container.scale.y = PostManager.post_width;

			this._container.add(post_container);
		}
		else
		{
			if (this._wraparoundPost)
			{
				this._container.remove(this._wraparoundPost.container);
			}
		}
	}

	/**
	 * Needs to be called after updatePosts because it uses the result of that.
	 */
	private updateTrim()
	{
		var metrics = this._metrics;

		//var trim_length = metrics.building_length + 0.2;
		var trim_back  = -metrics.building_length/2 + 0.1;
		var trim_front =  metrics.building_length/2 - 0.1;

		//trim_back  += metrics.cut_back;
		//trim_front -= metrics.cut_front;

		var wraparound_back  = this.config.wraparound_back.enabled.value;
		var wraparound_front = this.config.wraparound_front.enabled.value;

		if (wraparound_back)
		{
			trim_back -= this._config.wraparound_back.getOtherLeanTo(this._config).length.value;
		}

		if (wraparound_front)
		{
			trim_front += this._config.wraparound_front.getOtherLeanTo(this._config).length.value;
		}

		this._trimMetrics = {
			x: -metrics.length,
			top: metrics.wall_top,
			width: metrics.length,
			z_front: trim_front,
			z_back: trim_back,
			pitch: metrics.pitch,
			wraparound_back: wraparound_back,
			wraparound_front: wraparound_front,
			side: this.id,
			post_transforms: this._postManager.postTransforms,
			gutter_enabled: false,
			gutter_bottom: 0.9, // this determines how close the bottom of the gutter is to the ground
			gutter_length_padding: 0,
			gutter_wall_offset: 0
		};

		if (this._trim)
		{
			// 0.1 alignment "magic number" only to be used by trim (not LeanTo walls)
			var trimMetrics = ObjectUtils.clone(this._trimMetrics);
			trimMetrics.z_back  -= 0.1;
			trimMetrics.z_front += 0.1;

			this._trim.update(trimMetrics);
			Main.getInstance().config.building.gutter.setValue(Main.getInstance().config.building.gutter.value); // When you first added a leanto, then it had gutters, even when it was set to off. This is a workaround for that.
		}
	}

	private updateWraparounds()
	{
		this._wraparound_back.update();
		this._wraparound_front.update();
	}

	private getBuildingLength()
	{
		var buildingMetrics = this._building.config.metrics;

		if (this._isSide)
		{
			// lean to overhang matches building overhang if there's no drop,
			// matches building edge otherwise

			//if (this._config.drop.value > 0)
			//{
				// drop: match building edge
				return buildingMetrics.length;
			//}
			//else
			//{
				// no drop: match building overhang
			//	return buildingMetrics.length + buildingMetrics.overhang_end * 2;
			//}
		}
		else
		{
			return buildingMetrics.width;
		}
	}

	private getBuildingWidth()
	{
		var buildingMetrics = this._building.config.metrics;
		return this._isSide ? buildingMetrics.width : buildingMetrics.length;
	}

	public get postManager()
	{
		return this._postManager;
	}

	public get metrics()
	{
		return this._metrics;
	}

	public get config()
	{
		return this._config;
	}
}
